import React, { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { useAuth } from "../contexts/AuthContext";
import { DeleteConfirmDialog } from "../components/pipeline/DeleteConfirmDialog";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Badge } from "../components/ui/badge";
import { Label } from "../components/ui/label";
import { Switch } from "../components/ui/switch";
import { Textarea } from "../components/ui/textarea";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "../components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import { toast } from "sonner";
import {
  Megaphone,
  Plus,
  Edit2,
  Trash2,
  Eye,
  EyeOff,
  Info,
  AlertTriangle,
  CheckCircle2,
  Gift,
  Calendar,
  User,
} from "lucide-react";

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Announcement type configuration
const announcementTypes = [
  { id: "info", label: "Información", icon: Info, color: "bg-[#03BEE4]", textColor: "text-[#03BEE4]" },
  { id: "warning", label: "Advertencia", icon: AlertTriangle, color: "bg-orange-500", textColor: "text-orange-400" },
  { id: "success", label: "Éxito", icon: CheckCircle2, color: "bg-[#42E695]", textColor: "text-[#42E695]" },
  { id: "promo", label: "Promoción", icon: Gift, color: "bg-purple-500", textColor: "text-purple-400" },
];

const AnnouncementsPage = () => {
  const { user, canDelete } = useAuth();
  const [announcements, setAnnouncements] = useState([]);
  const [loading, setLoading] = useState(true);
  const [createOpen, setCreateOpen] = useState(false);
  const [editingAnnouncement, setEditingAnnouncement] = useState(null);
  const [typeFilter, setTypeFilter] = useState("all");
  const [showInactive, setShowInactive] = useState(true);
  const [deleteConfirm, setDeleteConfirm] = useState({ open: false, title: "", message: "", onConfirm: null });
  const [newAnnouncement, setNewAnnouncement] = useState({
    title: "",
    message: "",
    type: "info",
    target_roles: [],
    is_active: true,
    priority: 0,
  });

  const fetchAnnouncements = useCallback(async () => {
    try {
      const params = new URLSearchParams();
      params.append("include_inactive", showInactive);
      if (typeFilter !== "all") {
        params.append("announcement_type", typeFilter);
      }
      const response = await axios.get(`${API_URL}/api/announcements/all?${params.toString()}`);
      setAnnouncements(response.data);
    } catch (error) {
      console.error("Error fetching announcements:", error);
      toast.error("Error al cargar anuncios");
    } finally {
      setLoading(false);
    }
  }, [showInactive, typeFilter]);

  useEffect(() => {
    fetchAnnouncements();
  }, [fetchAnnouncements]);

  const handleCreateAnnouncement = async () => {
    if (!newAnnouncement.title.trim() || !newAnnouncement.message.trim()) {
      toast.error("El título y el mensaje son obligatorios");
      return;
    }
    try {
      await axios.post(`${API_URL}/api/announcements`, newAnnouncement);
      toast.success("Anuncio creado correctamente");
      setCreateOpen(false);
      setNewAnnouncement({
        title: "",
        message: "",
        type: "info",
        target_roles: [],
        is_active: true,
        priority: 0,
      });
      fetchAnnouncements();
    } catch (error) {
      console.error("Error creating announcement:", error);
      toast.error(error.response?.data?.detail || "Error al crear anuncio");
    }
  };

  const handleUpdateAnnouncement = async () => {
    if (!editingAnnouncement) return;
    try {
      await axios.put(`${API_URL}/api/announcements/${editingAnnouncement.id}`, editingAnnouncement);
      toast.success("Anuncio actualizado");
      setEditingAnnouncement(null);
      fetchAnnouncements();
    } catch (error) {
      console.error("Error updating announcement:", error);
      toast.error("Error al actualizar anuncio");
    }
  };

  const handleDeleteAnnouncement = async (announcementId, title) => {
    setDeleteConfirm({
      open: true,
      title: "Eliminar anuncio",
      message: `¿Estás seguro de eliminar el anuncio "${title}"?`,
      onConfirm: async () => {
        setDeleteConfirm(prev => ({ ...prev, open: false }));
        try {
          await axios.delete(`${API_URL}/api/announcements/${announcementId}`);
          toast.success("Anuncio eliminado");
          fetchAnnouncements();
        } catch (error) {
          console.error("Error deleting announcement:", error);
          toast.error("Error al eliminar anuncio");
        }
      },
    });
  };

  const handleToggleActive = async (announcement) => {
    try {
      await axios.put(`${API_URL}/api/announcements/${announcement.id}`, {
        ...announcement,
        is_active: !announcement.is_active,
      });
      toast.success(announcement.is_active ? "Anuncio desactivado" : "Anuncio activado");
      fetchAnnouncements();
    } catch (error) {
      console.error("Error toggling announcement:", error);
      toast.error("Error al cambiar estado del anuncio");
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return "-";
    return new Date(dateStr).toLocaleDateString("es-ES", {
      day: "2-digit",
      month: "short",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-[#42E695]"></div>
      </div>
    );
  }

  const activeCount = announcements.filter(a => a.is_active).length;
  const inactiveCount = announcements.filter(a => !a.is_active).length;

  return (
    <>
    <div className="space-y-6" data-testid="announcements-page">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h1 className="text-2xl md:text-3xl font-heading font-semibold text-white flex items-center gap-3">
            <Megaphone className="h-8 w-8 text-[#03BEE4]" />
            Gestión de Anuncios
          </h1>
          <p className="text-slate-400 mt-1">
            Crea y administra avisos y noticias para los usuarios
          </p>
        </div>
        <Dialog open={createOpen} onOpenChange={setCreateOpen}>
          <DialogTrigger asChild>
            <Button className="bg-[#42E695] text-[#04042C] hover:bg-[#34D399]" data-testid="create-announcement-btn">
              <Plus className="h-4 w-4 mr-2" />
              Nuevo Anuncio
            </Button>
          </DialogTrigger>
          <DialogContent className="bg-[#0A0A35] border-[#1E1E5F] text-white max-w-lg">
            <DialogHeader>
              <DialogTitle className="text-white font-heading flex items-center gap-2">
                <Megaphone className="h-5 w-5 text-[#03BEE4]" />
                Crear Nuevo Anuncio
              </DialogTitle>
            </DialogHeader>
            <div className="space-y-4 mt-4">
              <div>
                <Label className="text-slate-300">Título *</Label>
                <Input
                  value={newAnnouncement.title}
                  onChange={(e) => setNewAnnouncement({ ...newAnnouncement, title: e.target.value })}
                  placeholder="Ej: Nueva funcionalidad disponible"
                  className="mt-1 bg-[#0A0A35] border-[#1E1E5F] text-white"
                  data-testid="announcement-title-input"
                />
              </div>
              <div>
                <Label className="text-slate-300">Mensaje *</Label>
                <Textarea
                  value={newAnnouncement.message}
                  onChange={(e) => setNewAnnouncement({ ...newAnnouncement, message: e.target.value })}
                  placeholder="Describe el anuncio con detalle..."
                  rows={4}
                  className="mt-1 bg-[#0A0A35] border-[#1E1E5F] text-white"
                  data-testid="announcement-message-input"
                />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="text-slate-300">Tipo</Label>
                  <Select
                    value={newAnnouncement.type}
                    onValueChange={(v) => setNewAnnouncement({ ...newAnnouncement, type: v })}
                  >
                    <SelectTrigger className="mt-1 bg-[#0A0A35] border-[#1E1E5F] text-white">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent className="bg-[#0A0A35] border-[#1E1E5F]">
                      {announcementTypes.map((type) => (
                        <SelectItem key={type.id} value={type.id} className="text-white">
                          <div className="flex items-center gap-2">
                            <type.icon className="h-4 w-4" />
                            {type.label}
                          </div>
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-slate-300">Prioridad (0-100)</Label>
                  <Input
                    type="number"
                    min="0"
                    max="100"
                    value={newAnnouncement.priority}
                    onChange={(e) => setNewAnnouncement({ ...newAnnouncement, priority: parseInt(e.target.value) || 0 })}
                    className="mt-1 bg-[#0A0A35] border-[#1E1E5F] text-white"
                  />
                  <p className="text-xs text-slate-500 mt-1">Mayor número = más arriba</p>
                </div>
              </div>
              <div className="flex items-center gap-3 pt-2">
                <Switch
                  checked={newAnnouncement.is_active}
                  onCheckedChange={(checked) => setNewAnnouncement({ ...newAnnouncement, is_active: checked })}
                />
                <Label className="text-slate-300">Activo (visible en Dashboard)</Label>
              </div>
              <div className="flex justify-end gap-3 pt-4 border-t border-[#1E1E5F]">
                <Button variant="outline" onClick={() => setCreateOpen(false)} className="border-[#1E1E5F] text-slate-300">
                  Cancelar
                </Button>
                <Button onClick={handleCreateAnnouncement} className="bg-[#42E695] text-[#04042C]" data-testid="save-announcement-btn">
                  Crear Anuncio
                </Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Card className="bg-[#0A0A35] border-[#1E1E5F]">
          <CardContent className="pt-4 pb-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-[#03BEE4]/20 flex items-center justify-center">
                <Megaphone className="h-5 w-5 text-[#03BEE4]" />
              </div>
              <div>
                <p className="text-slate-400 text-xs">Total Anuncios</p>
                <p className="text-xl font-bold text-[#03BEE4]">{announcements.length}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="bg-[#0A0A35] border-[#1E1E5F]">
          <CardContent className="pt-4 pb-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-[#42E695]/20 flex items-center justify-center">
                <Eye className="h-5 w-5 text-[#42E695]" />
              </div>
              <div>
                <p className="text-slate-400 text-xs">Activos</p>
                <p className="text-xl font-bold text-[#42E695]">{activeCount}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="bg-[#0A0A35] border-[#1E1E5F]">
          <CardContent className="pt-4 pb-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-slate-500/20 flex items-center justify-center">
                <EyeOff className="h-5 w-5 text-slate-400" />
              </div>
              <div>
                <p className="text-slate-400 text-xs">Inactivos</p>
                <p className="text-xl font-bold text-slate-400">{inactiveCount}</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Filters */}
      <Card className="bg-[#0A0A35] border-[#1E1E5F]">
        <CardContent className="pt-4 pb-4">
          <div className="flex flex-wrap items-center gap-4">
            <div className="flex items-center gap-2">
              <Label className="text-slate-400 text-sm">Filtrar por tipo:</Label>
              <Select value={typeFilter} onValueChange={setTypeFilter}>
                <SelectTrigger className="w-[180px] bg-[#1B1B4B] border-[#1E1E5F] text-white">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-[#0A0A35] border-[#1E1E5F]">
                  <SelectItem value="all" className="text-white">Todos los tipos</SelectItem>
                  {announcementTypes.map((type) => (
                    <SelectItem key={type.id} value={type.id} className="text-white">
                      {type.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="flex items-center gap-2">
              <Switch
                checked={showInactive}
                onCheckedChange={setShowInactive}
                className="data-[state=checked]:bg-[#42E695]"
              />
              <Label className="text-slate-400 text-sm">Mostrar inactivos</Label>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Announcements List */}
      <Card className="bg-[#0A0A35] border-[#1E1E5F]">
        <CardHeader>
          <CardTitle className="text-white font-heading flex items-center gap-2">
            <Megaphone className="h-5 w-5 text-[#03BEE4]" />
            Anuncios
          </CardTitle>
          <CardDescription className="text-slate-400">
            Los anuncios activos se muestran en el Dashboard de los usuarios
          </CardDescription>
        </CardHeader>
        <CardContent>
          {announcements.length === 0 ? (
            <div className="p-8 text-center border border-dashed border-[#1E1E5F] rounded-lg">
              <Megaphone className="h-12 w-12 text-slate-500 mx-auto mb-4" />
              <p className="text-slate-400 mb-2">No hay anuncios para mostrar</p>
              <p className="text-slate-500 text-sm">Ajusta los filtros o crea nuevos anuncios</p>
            </div>
          ) : (
            <div className="space-y-4">
              {announcements
                .sort((a, b) => b.priority - a.priority)
                .map((announcement) => {
                  const typeConfig = announcementTypes.find(t => t.id === announcement.type) || announcementTypes[0];
                  const TypeIcon = typeConfig.icon;
                  
                  return (
                    <div 
                      key={announcement.id} 
                      className={`p-4 rounded-lg border transition-all ${
                        announcement.is_active 
                          ? 'bg-[#1B1B4B]/50 border-[#1E1E5F] hover:border-[#42E695]/30' 
                          : 'bg-slate-800/30 border-slate-700 opacity-60'
                      }`}
                      data-testid={`announcement-item-${announcement.id}`}
                    >
                      <div className="flex items-start justify-between gap-4">
                        <div className="flex items-start gap-3 flex-1">
                          <div className={`p-2 rounded-lg ${typeConfig.color}/20`}>
                            <TypeIcon className={`h-5 w-5 ${typeConfig.textColor}`} />
                          </div>
                          <div className="flex-1">
                            <div className="flex items-center gap-2 flex-wrap">
                              <h4 className="text-white font-medium">{announcement.title}</h4>
                              <Badge className={`${typeConfig.color} text-white text-xs`}>
                                {typeConfig.label}
                              </Badge>
                              {!announcement.is_active && (
                                <Badge className="bg-slate-600 text-slate-300 text-xs">Inactivo</Badge>
                              )}
                              <Badge className="bg-[#1E1E5F] text-slate-400 text-xs">
                                Prioridad: {announcement.priority}
                              </Badge>
                            </div>
                            <p className="text-slate-400 text-sm mt-2">{announcement.message}</p>
                            <div className="flex items-center gap-4 mt-3 text-xs text-slate-500">
                              <span className="flex items-center gap-1">
                                <User className="h-3 w-3" />
                                {announcement.created_by_name || "Sistema"}
                              </span>
                              <span className="flex items-center gap-1">
                                <Calendar className="h-3 w-3" />
                                {formatDate(announcement.created_at)}
                              </span>
                            </div>
                          </div>
                        </div>
                        <div className="flex items-center gap-2">
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => handleToggleActive(announcement)}
                            className={announcement.is_active ? "text-[#42E695] hover:text-[#42E695]/80" : "text-slate-400 hover:text-slate-300"}
                            title={announcement.is_active ? "Desactivar" : "Activar"}
                            data-testid={`toggle-announcement-${announcement.id}`}
                          >
                            {announcement.is_active ? <Eye className="h-4 w-4" /> : <EyeOff className="h-4 w-4" />}
                          </Button>
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => setEditingAnnouncement({ ...announcement })}
                            className="text-[#03BEE4] hover:text-[#03BEE4]/80"
                            data-testid={`edit-announcement-${announcement.id}`}
                          >
                            <Edit2 className="h-4 w-4" />
                          </Button>
                          {canDelete("announcements") && (
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => handleDeleteAnnouncement(announcement.id, announcement.title)}
                            className="text-red-400 hover:text-red-300"
                            data-testid={`delete-announcement-${announcement.id}`}
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                          )}
                        </div>
                      </div>
                    </div>
                  );
                })}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Edit Announcement Dialog */}
      {editingAnnouncement && (
        <Dialog open={!!editingAnnouncement} onOpenChange={() => setEditingAnnouncement(null)}>
          <DialogContent className="bg-[#0A0A35] border-[#1E1E5F] text-white max-w-lg">
            <DialogHeader>
              <DialogTitle className="text-white font-heading flex items-center gap-2">
                <Edit2 className="h-5 w-5 text-[#03BEE4]" />
                Editar Anuncio
              </DialogTitle>
            </DialogHeader>
            <div className="space-y-4 mt-4">
              <div>
                <Label className="text-slate-300">Título *</Label>
                <Input
                  value={editingAnnouncement.title}
                  onChange={(e) => setEditingAnnouncement({ ...editingAnnouncement, title: e.target.value })}
                  className="mt-1 bg-[#0A0A35] border-[#1E1E5F] text-white"
                />
              </div>
              <div>
                <Label className="text-slate-300">Mensaje *</Label>
                <Textarea
                  value={editingAnnouncement.message}
                  onChange={(e) => setEditingAnnouncement({ ...editingAnnouncement, message: e.target.value })}
                  rows={4}
                  className="mt-1 bg-[#0A0A35] border-[#1E1E5F] text-white"
                />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="text-slate-300">Tipo</Label>
                  <Select
                    value={editingAnnouncement.type}
                    onValueChange={(v) => setEditingAnnouncement({ ...editingAnnouncement, type: v })}
                  >
                    <SelectTrigger className="mt-1 bg-[#0A0A35] border-[#1E1E5F] text-white">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent className="bg-[#0A0A35] border-[#1E1E5F]">
                      {announcementTypes.map((type) => (
                        <SelectItem key={type.id} value={type.id} className="text-white">
                          <div className="flex items-center gap-2">
                            <type.icon className="h-4 w-4" />
                            {type.label}
                          </div>
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-slate-300">Prioridad (0-100)</Label>
                  <Input
                    type="number"
                    min="0"
                    max="100"
                    value={editingAnnouncement.priority}
                    onChange={(e) => setEditingAnnouncement({ ...editingAnnouncement, priority: parseInt(e.target.value) || 0 })}
                    className="mt-1 bg-[#0A0A35] border-[#1E1E5F] text-white"
                  />
                </div>
              </div>
              <div className="flex items-center gap-3 pt-2">
                <Switch
                  checked={editingAnnouncement.is_active}
                  onCheckedChange={(checked) => setEditingAnnouncement({ ...editingAnnouncement, is_active: checked })}
                />
                <Label className="text-slate-300">Activo (visible en Dashboard)</Label>
              </div>
              <div className="flex justify-end gap-3 pt-4 border-t border-[#1E1E5F]">
                <Button variant="outline" onClick={() => setEditingAnnouncement(null)} className="border-[#1E1E5F] text-slate-300">
                  Cancelar
                </Button>
                <Button onClick={handleUpdateAnnouncement} className="bg-[#42E695] text-[#04042C]">
                  Guardar Cambios
                </Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>
      )}
    </div>

    <DeleteConfirmDialog
      open={deleteConfirm.open}
      onOpenChange={(open) => !open && setDeleteConfirm(prev => ({ ...prev, open: false }))}
      onConfirm={deleteConfirm.onConfirm}
      onCancel={() => setDeleteConfirm(prev => ({ ...prev, open: false }))}
      title={deleteConfirm.title}
      message={deleteConfirm.message}
    />
    </>
  );
};

export default AnnouncementsPage;
